import { useCallback, useRef, useState } from "react";
import { getApiErrorMessage } from "~/lib/api";
import { Button } from "~/components/ui/button";
import { FileDropzone } from "~/components/ui/file-dropzone";
import { Icon } from "~/components/ui/icons/icon";
import { toast } from "~/components/ui/toaster";

interface TrackUploaderProps {
  track?: GeoJSON.LineString | null;
  onUpload: (file: File) => Promise<void>;
  onDelete?: () => Promise<void>;
}

export function TrackUploader({ track, onUpload, onDelete }: TrackUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = useCallback(
    async (file: File) => {
      setUploading(true);
      try {
        await onUpload(file);
        toast.success("GPX track uploaded");
      } catch (err) {
        toast.error(getApiErrorMessage(err, "Failed to upload GPX"));
      } finally {
        setUploading(false);
      }
    },
    [onUpload],
  );

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) await handleUpload(file);
      e.target.value = "";
    },
    [handleUpload],
  );

  const handleDelete = useCallback(async () => {
    if (!onDelete) return;
    setDeleting(true);
    try {
      await onDelete();
      toast.success("Track removed");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to remove track"));
    } finally {
      setDeleting(false);
    }
  }, [onDelete]);

  const busy = uploading || deleting;

  if (track) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <input ref={fileInputRef} type="file" accept=".gpx" onChange={handleFileChange} className="hidden" />
        <Icon i="check" className="size-4 text-primary" />
        <span className="flex-1 text-muted-foreground">GPX track uploaded</span>
        <Button
          variant="outline"
          size="sm"
          className="shadow-none"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
        >
          Replace
        </Button>
        {onDelete && (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={handleDelete}
            disabled={busy}
            aria-label="Remove track"
            title="Remove track"
          >
            <Icon i="trash" className="size-4" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <FileDropzone
      onFilesSelect={(files) => files[0] && handleUpload(files[0])}
      accept=".gpx"
      disabled={uploading}
      label={uploading ? "Uploading..." : "Drop GPX file or click to upload"}
      dragLabel="Drop GPX file here"
    />
  );
}
